import Head from 'next/head'

import { Case, FuneralHome, ObituarySnippet } from '../../types'
import UrlModel from '../models/UrlModel'

interface ObituaryMetaProps {
  obituary: ObituarySnippet | Case
  funeralHome: FuneralHome | null
  pageTitle: string
  pageDescription: string
  canonicalPath: string
  ogDescription?: string
}

const getAbsoluteImageUrl = (image: string | null): string => {
  const defaultImage = UrlModel.fullURL('/default-obit.jpg')

  if (!image) {
    return defaultImage
  }

  if (image.startsWith('http')) {
    return image
  }

  return UrlModel.fullURL(image)
}

const ObituaryMeta: React.FC<ObituaryMetaProps> = ({
  obituary,
  funeralHome,
  pageTitle,
  pageDescription,
  canonicalPath,
  ogDescription,
}) => {
  const imageUrl = getAbsoluteImageUrl(obituary.deceased.image)
  const canonicalUrl = UrlModel.fullURL(canonicalPath)
  const siteName = funeralHome?.name || 'Kalas Funeral Home'
  const socialDescription = ogDescription || pageDescription

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} key="description" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={socialDescription} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />
    </Head>
  )
}

export default ObituaryMeta
